// ============================================================================
// CUSTOS — Pure transform functions: API / on-chain types → UI types
// No Angular dependencies. Every function is pure and side-effect-free.
// ============================================================================

import { formatUnits } from 'viem';

import type {
  ApiCycle,
  ApiLockerOrder,
  ApiMarket,
  ApiOrder,
  ApiOrderSettlement,
  CycleState,
  MarketConfig,
} from '../api/api.types';

import type {
  ActivityEntry,
  HomeActivity,
  MarketCardData,
  PredictionActive,
  PredictionClosed,
  PredictionMarket,
  PortfolioReward,
  RollockHistoryEntry,
  RollockItem,
} from '../../shared/models';

// ============================================================================
// UTILITY
// ============================================================================

/** Derive a resolver link from price_source when no explicit link is configured. */
function deriveResolverLink(config: MarketConfig | null | undefined): string | null {
  if (!config?.price_source) return null;
  const pair = config.quote_pair; // e.g. "BTC-USDT"
  switch (config.price_source) {
    case 'binance':
      if (pair) {
        const symbol = pair.replace('-', '_').toUpperCase(); // "BTC_USDT"
        return `https://www.binance.com/en/trade/${symbol}`;
      }
      return null;
    case 'okx':
      return pair ? `https://www.okx.com/en-us/trade-spot/${pair.toLowerCase()}` : null;
    default:
      return null;
  }
}

/** Convert a bigint wei value to a human-readable number. */
export function weiToHuman(value: bigint, decimals: number): number {
  return Number(formatUnits(value, decimals));
}

/**
 * Single source of truth for "what does this user's locker hold and what
 * can they do with it right now?" derived from the contract's allLocks()
 * tuple [stakeWei, endTime, lockTime]. Wesley 2026-05-25: post-expiry the
 * capital migrates from Staking to Withdrawable — it must NEVER appear in
 * both columns, and the bet dialog must NOT advertise it as spendable.
 *
 * Pre-expiry: spendableStaking = raw, withdrawable = 0.
 * Post-expiry: spendableStaking = 0,  withdrawable = raw.
 *
 * Centralising this here closes a recurring bug: three independent call
 * sites (portfolio table, bet dialog, rollock detail) each re-derived the
 * semantic differently, and at least one regression test encoded the
 * wrong shape. Future readers — keep all three on this helper.
 *
 * `rawLocked` / `rawLockedWei` are exposed only for the relock and
 * withdraw flows that genuinely need the contract amount independent of
 * expiry state. Never bind them directly to a Staking column.
 */
export function deriveLockerAmounts(
  lockInfo: readonly [bigint, number, number],
  decimals: number,
): {
  readonly rawLockedWei: bigint;
  readonly rawLocked: number;
  readonly endTime: number;
  readonly isExpired: boolean;
  readonly spendableStaking: number;
  readonly withdrawable: number;
} {
  const rawLockedWei = lockInfo[0];
  const rawLocked = weiToHuman(rawLockedWei, decimals);
  const endTime = Number(lockInfo[1]);
  const isExpired = endTime > 0 && Date.now() / 1000 > endTime;
  return {
    rawLockedWei,
    rawLocked,
    endTime,
    isExpired,
    spendableStaking: isExpired ? 0 : rawLocked,
    withdrawable: isExpired ? rawLocked : 0,
  };
}

/**
 * Truncate a wei value to 2-decimal ("cent") granularity at the BigInt
 * level, THEN convert to a human-readable number. Guarantees that
 * parseUnits(result, decimals) round-trips to a wei value ≤ the input,
 * which is what the MAX button needs to avoid "amount > balance" reverts
 * caused by IEEE-754 precision loss in Number(formatUnits(...)).
 *
 * Input 13271859999999999999999n (18 dec) → 13271.85 (not 13271.86).
 */
export function truncateWeiToCents(value: bigint, decimals: number): number {
  if (decimals < 2) return weiToHuman(value, decimals);
  const centUnit = 10n ** BigInt(decimals - 2);
  const truncated = (value / centUnit) * centUnit;
  return weiToHuman(truncated, decimals);
}

/**
 * Heuristically convert a backend numeric field into a human-readable decimal
 * string. Backends in this system ship values in three shapes:
 *
 *   1. Already-human with genuine fraction:  "78014.18942"   → keep as-is
 *   2. Scientific notation:                  "7.8e22"        → keep as-is
 *   3. Raw wei (large integer), optionally
 *      decorated with a zero-only tail:      "78014189420000000000000"
 *                                            "78014189420000000000000.0"
 *                                                            → formatUnits / 10^decimals
 *
 * Idempotent: already-human values pass through unchanged so staging builds
 * don't double-divide. Small integers below 10^(decimals/2) are treated as
 * already-human even without a decimal point (guards against e.g. pre-divided
 * "100" being treated as 1e-16 wei).
 *
 * Returns null only for null/undefined/empty input. Invalid-looking strings
 * fall back to the raw input string.
 */
export function normalizeWeiString(
  raw: string | number | null | undefined,
  decimals = 18,
): string | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const s = String(raw);
  // Scientific notation — trust backend, pass through.
  if (/[eE]/.test(s)) return s;
  // `.0`, `.00000` style trailing-zero tails are noise — the backend adds
  // them to raw wei values. Strip pure-zero fractions; keep genuine decimals.
  const cleaned = s.replace(/\.0+$/, '');
  if (cleaned.includes('.')) {
    // Non-zero fractional part → already human.
    return s;
  }
  try {
    const bi = BigInt(cleaned);
    // Below threshold → can't be wei-scale; treat as already-human integer.
    if (bi < 10n ** BigInt(Math.floor(decimals / 2))) return cleaned;
    return formatUnits(bi, decimals);
  } catch {
    return s;
  }
}

/**
 * Deterministic boundary divisor: divides `value` by 10^scale.
 *
 * Used at the api.service ingestion boundary to invert the contract's
 * over-scaling for non-18-dec tokens. Two scaling regimes are encoded by the
 * call site:
 *   - Pool totals + per-order amount/share/reward → scale = 18 - decimals
 *     (so 18-dec markets get scale=0 / no-op; 6-dec USDT gets scale=12).
 *   - Prize amount + price feeds → scale = 18 (always).
 *
 * Handles both integer raw ("2000000000000") and fractional pre-normalized
 * ("52886026259240.002167") shapes — the latter occurs on settlement.reward.
 * Returns null only for null/undefined/empty input. Falls back to the input
 * string on Number-parse failure (defensive against unexpected shapes).
 */
export function divideAtScale(
  value: string | null | undefined,
  scale: number,
): string | null {
  if (value === null || value === undefined || value === '') return null;
  const s = String(value);
  if (scale <= 0) return s;
  const num = Number(s);
  if (!Number.isFinite(num)) return s;
  return String(num / Math.pow(10, scale));
}

/**
 * Shared fraction-digits policy for price / dollar values.
 * - |v| >= 1 or v === 0 → 2dp (e.g. \$33.56, \$0.00)
 * - |v| < 1 → min 6dp (Wesley: "剩六位數"), extra digits to preserve 5 sig figs
 *   at the first non-zero digit, capped at 10 so tails don't balloon.
 * Single source of truth — chart Y-axis, chart tooltip, detail header,
 * prediction title all call this so they move in lockstep.
 */
export function adaptiveDecimalDigits(value: number): number {
  const abs = Math.abs(value);
  if (abs === 0 || abs >= 1) return 2;
  const leadingZeros = Math.floor(-Math.log10(abs));
  return Math.min(Math.max(leadingZeros + 5, 6), 10);
}

/** Map API cycle state to UI status. */
export function cycleStateToStatus(
  state: string,
): 'active_entry' | 'cooldown' | 'settled' {
  switch (state) {
    case 'ACTIVE':
    case 'BETTING':
      return 'active_entry';
    case 'BETTING_ENDED':
      return 'cooldown';
    case 'SETTLED':
    case 'RESOLVED':
      return 'settled';
    case 'SCHEDULED':
    case 'CREATING':
      return 'cooldown';
    default:
      return 'cooldown';
  }
}

/** Return "HH:MM:SS" countdown from a unix timestamp (seconds). */
export function computeCountdown(endTimeUnix: number): string {
  if (!Number.isFinite(endTimeUnix)) return '--:--:--';
  const nowSec = Math.floor(Date.now() / 1000);
  const diff = endTimeUnix - nowSec;
  if (diff <= 0) return '00:00:00';

  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;

  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

// ============================================================================
// WIN MULTIPLIER
// ============================================================================

/** Tunable constants for the Win Multiplier formula. */
const WIN_MULT = {
  alpha: 0.80,   // time decay exponent
  beta: 1.20,    // follow penalty exponent
  gamma: 1.00,   // coldness curve exponent
  kappa: 1.00,   // overall strength
  rho: 0.80,     // extra weight for coldness
  G: 60,         // minutes to reach max coldness
  B_max: 1.80,   // cap for contrarian bonus
  M_max: 3.00,   // hard cap for multiplier
};

export interface WinMultiplierParams {
  side: 'YES' | 'NO';
  yesPool: number;
  noPool: number;
  /** Hours remaining until betting deadline. */
  hoursRemaining: number;
  /** Total betting window in hours (start → deadline). */
  totalBettingHours: number;
  /** Minutes since last valid order on this market. 0 if unknown. */
  minutesSinceLastOrder?: number;
}

/**
 * Compute the projected Win Multiplier for a new bet.
 *
 * Formula: m = MIN(M_max, MAX(1, 1 + κ · A(r) · B(p) · (1 + ρ · C(u))))
 *
 * - A(r) = r^α           — time decay (earlier bets → higher)
 * - B(p) = MIN(B_max, ((1-p)/0.5)^β)  — contrarian bonus (minority side → higher)
 * - C(u) = u^γ           — coldness bonus (cold market → higher)
 */
export function computeWinMultiplier(params: WinMultiplierParams): number {
  const { alpha, beta, gamma, kappa, rho, G, B_max, M_max } = WIN_MULT;
  const { side, yesPool, noPool, hoursRemaining, totalBettingHours, minutesSinceLastOrder = 0 } = params;

  const total = yesPool + noPool;

  // p = chosen side's share of total pool
  // When both pools are empty (first bet), default to 0.5 (even split)
  const p = total === 0 ? 0.5 : (side === 'YES' ? yesPool : noPool) / total;

  // r = normalized time remaining (1 = start, 0 = deadline)
  const r = totalBettingHours > 0
    ? Math.max(0, Math.min(1, hoursRemaining / totalBettingHours))
    : 0;

  // u = normalized coldness (0 = just had an order, 1 = G minutes since last)
  const u = Math.min(1, minutesSinceLastOrder / G);

  const A = Math.pow(r, alpha);
  const B = Math.min(B_max, Math.pow((1 - p) / 0.5, beta));
  const C = Math.pow(u, gamma);

  return Math.min(M_max, Math.max(1, 1 + kappa * A * B * (1 + rho * C)));
}

/** Format a settlement date to "MMM d, HH:mm" (e.g. "Mar 23, 00:10"). */
export function formatSettlementDate(date: Date): string {
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${month} ${day}, ${hours}:${minutes}`;
}

/** Format a number to compact form: "12.3k", "1.2M", etc. */
export function formatVolume(value: number): string {
  if (value === 0) return '0';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return value.toFixed(2);
}

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

/**
 * Parse a market_id like "CTS_BTC" into base/quote symbols.
 * Falls back to the raw string if no underscore is found.
 */
export function parseMarketId(marketId: string): { base: string; quote: string } {
  // market_id format: "CTS-BTC" or "CTS_BTC" — base=staking token, quote=predicted asset
  const sep = marketId.includes('-') ? '-' : '_';
  const idx = marketId.indexOf(sep);
  if (idx === -1) return { base: marketId, quote: '' };
  return { base: marketId.slice(0, idx), quote: marketId.slice(idx + 1) };
}

/** Render the cycle's strike price portion of the prediction title —
 *  "Will $X be above $Y". Returns "$?" when no strike is set yet. */
function buildTitlePrefix(marketId: string, cycle: ApiCycle): string {
  const { quote } = parseMarketId(marketId);
  const price = safeNum(cycle.start_price);
  if (price <= 0) return `Will $${quote} be above $?`;
  const fractionDigits = adaptiveDecimalDigits(price);
  const priceStr = price.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return `Will $${quote} be above $${priceStr}`;
}

/** Prediction title for cards — ends with "on" so the template can append
 *  the settlement date in its own DOM element. */
function buildTitle(marketId: string, cycle: ApiCycle): string {
  return `${buildTitlePrefix(marketId, cycle)} on`;
}

/** Full-text prediction title including the settlement date.
 *  e.g. "Will $BTC be above $78,014.19 on Apr 23, 18:00?" */
export function buildCycleTitle(marketId: string, cycle: ApiCycle): string {
  const dateStr = formatSettlementDate(new Date(cycle.end_time));
  return `${buildTitlePrefix(marketId, cycle)} on ${dateStr}?`;
}

/** Safely convert a value (number or string) to BigInt. Handles large integers from JSON.parse. */
export function safeBigInt(value: string | number | bigint | null | undefined): bigint {
  if (value == null) return 0n;
  if (typeof value === 'bigint') return value;
  // Convert to string first to avoid Number precision loss on large integers
  const str = String(value);
  // Handle scientific notation from JSON.parse (e.g., "7.111786e+22")
  if (str.includes('e') || str.includes('E')) {
    return BigInt(Math.round(Number(str)));
  }
  try { return BigInt(str.split('.')[0]); } catch { return 0n; }
}

/** Safely parse a numeric string, returning 0 when empty/null. */
/**
 * Compute win multiplier from share and amount.
 * Handles both pre-divided (share=196, amount=100 → 1.96) and
 * raw wei mismatch (share=196e18, amount=100 → normalize by 1e18).
 */
export function computeShareMultiplier(share: string | number, amount: string | number): number {
  const s = Number(share) || 0;
  const a = Number(amount) || 0;
  if (a === 0 || s === 0) return 1.0;
  const ratio = s / a;
  // If ratio > 100, likely wei/human mismatch — divide by 1e18
  if (ratio > 100) return ratio / 1e18;
  return ratio;
}

export function safeNum(value: string | null | undefined): number {
  if (!value) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}


/** Parse a backend timestamp string (ISO / SQL) to epoch ms. Returns 0 on
 *  failure so callers can safely use it as a sort key (NaN breaks sort). */
export function toEpochMs(raw: string | null | undefined): number {
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Newest-first sort on any row shape that carries a numeric sortTimestamp. */
export function sortByTimeDesc<T extends { sortTimestamp: number }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => b.sortTimestamp - a.sortTimestamp);
}

/** Compute yes percentage from up/down totals. Returns 50 if both are zero. */
function computeYesPercent(upTotal: string, downTotal: string, decimals = 18): number {
  const up = safeNum(upTotal);
  const down = safeNum(downTotal);
  const total = up + down;
  if (total === 0) return 50;
  return Math.round((up / total) * 100);
}

/** Determine winner from cycle result. */
function resultToWinner(
  result: 0 | 1 | 2 | null,
): 'yes' | 'no' | null {
  if (result === 1) return 'yes';
  if (result === 2) return 'no';
  return null;
}

/** Shorten a wallet address to "0x1234…abcd" format. */
function shortenAddress(address: string): string {
  if (address.length <= 10) return address;
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

/** Map an order side string to a UI direction label. */
function sideToDirection(side: 'UP' | 'DOWN'): 'Yes' | 'No' {
  return side === 'UP' ? 'Yes' : 'No';
}

/** Determine the betting end time unix (seconds) from a cycle. */
function bettingEndUnix(cycle: ApiCycle): number {
  return Math.floor(new Date(cycle.betting_end_time).getTime() / 1000);
}

/** Determine the end time unix (seconds) from a cycle. */
function endUnix(cycle: ApiCycle): number {
  return Math.floor(new Date(cycle.end_time).getTime() / 1000);
}

// ============================================================================
// MARKET TRANSFORMS
// ============================================================================

/** Convert API market + cycle data into a MarketCardData for the prediction card. */
export function apiMarketAndCycleToMarketCard(
  market: ApiMarket,
  cycle: ApiCycle,
): MarketCardData {
  const { base, quote } = parseMarketId(market.market_id);
  const decimals = cycle.base_token_decimals ?? 18;
  const yesPercent = computeYesPercent(cycle.side_up_total, cycle.side_down_total, decimals);
  const status = cycleStateToStatus(cycle.state);

  const upHuman = safeNum(cycle.side_up_total);
  const downHuman = safeNum(cycle.side_down_total);
  const volume = formatVolume(upHuman + downHuman);

  // Use betting_end_time for active, end_time for cooldown
  const countdownTarget =
    status === 'active_entry' ? bettingEndUnix(cycle) : endUnix(cycle);

  return {
    id: market.market_id,
    title: market.title ?? buildTitle(market.market_id, cycle),
    tokenSymbol: quote || market.token_symbol || '—',
    baseTokenSymbol: base || '—',
    settlementDate: formatSettlementDate(new Date(cycle.end_time)),
    yesPercent,
    noPercent: 100 - yesPercent,
    volume,
    source: market.config_json?.price_source ?? 'binance',
    countdown: computeCountdown(countdownTarget),
    countdownTarget,
    cycleEndTarget: endUnix(cycle),
    status,
    winner: resultToWinner(cycle.result),
  };
}

/** Convert API market + cycle data into a full PredictionMarket model. */
export function apiMarketAndCycleToPredictionMarket(
  market: ApiMarket,
  cycle: ApiCycle,
): PredictionMarket {
  const { base, quote } = parseMarketId(market.market_id);
  const decimals = cycle.base_token_decimals ?? 18;
  const upNum = safeNum(cycle.side_up_total);
  const downNum = safeNum(cycle.side_down_total);
  const total = upNum + downNum;
  const yesPercent = computeYesPercent(cycle.side_up_total, cycle.side_down_total, decimals);

  return {
    id: market.market_id,
    title: market.title ?? buildTitle(market.market_id, cycle),
    tokenSymbol: quote || market.token_symbol || '—',
    baseTokenSymbol: base || '—',
    predictionContract: market.prediction_contract,
    description: market.description ?? '',
    targetPrice: safeNum(cycle.start_price),
    settlementDate: new Date(cycle.end_time),
    currentPrice: cycle.final_price ? safeNum(cycle.final_price) : undefined,
    status: cycleStateToStatus(cycle.state),
    // Static pool ratio — current multiplier per unit if no more bets come in.
    // For projected payout with a new bet X: (X + upNum + downNum) / (X + sideNum)
    yesOdds: total > 0 && upNum > 0 ? total / upNum : 0,
    noOdds: total > 0 && downNum > 0 ? total / downNum : 0,
    yesPercent,
    noPercent: 100 - yesPercent,
    totalVolume: total,
    totalUsers: 0, // Not available from API cycle data
    yesAmount: upNum,
    noAmount: downNum,
    winner: resultToWinner(cycle.result),
    bettingEndDate: new Date(cycle.betting_end_time),
    imageUrl: market.icon_url ?? undefined,
    resolverLink: market.config_json?.link ?? deriveResolverLink(market.config_json) ?? undefined,
    createdAt: new Date(cycle.start_time),
    updatedAt: cycle.resolved_at ? new Date(cycle.resolved_at) : new Date(cycle.start_time),
  };
}

// ============================================================================
// ACTIVITY TRANSFORMS
// ============================================================================

/** Convert an API order to an ActivityEntry (market / rollock detail pages). */
export function apiOrderToActivityEntry(order: ApiOrder): ActivityEntry {
  const decimals = order.base_token_decimals ?? 18;
  return {
    text: `${shortenAddress(order.user_address)} bet ${sideToDirection(order.side)}`,
    orderTime: new Date(order.created_at).toLocaleString(),
    direction: sideToDirection(order.side),
    amount: safeNum(order.amount),
    betFrom: order.from_locker ? 'Rollocker' : 'Wallet',
    user: shortenAddress(order.user_address),
  };
}

/** Convert an API order to a HomeActivity feed entry. */
export function apiOrderToHomeActivity(
  order: ApiOrder,
): HomeActivity {
  const marketId = order.market_id;
  const { base } = parseMarketId(marketId);
  const decimals = order.base_token_decimals ?? 18;
  const amount = safeNum(order.amount);

  return {
    wallet: shortenAddress(order.user_address),
    amount: `+${formatVolume(amount)}`,
    type: 'predict',
    tokenIcon: base,
    time: new Date(order.created_at).toLocaleString(),
    sortTime: order.created_at,
    route: `/predictions/${marketId}`,
  };
}

/** Convert a locker order to a HomeActivity feed entry (rollock type). */
export function apiLockerOrderToHomeActivity(
  order: ApiLockerOrder,
): HomeActivity {
  return {
    wallet: shortenAddress(order.user_address),
    amount: `+${formatVolume(safeNum(order.amount))}`,
    type: 'rollock',
    tokenIcon: order.locker_id,
    time: new Date(order.created_at).toLocaleString(),
    sortTime: order.created_at,
    route: '/rollock',
  };
}

// ============================================================================
// ROLLOCK TRANSFORMS
// ============================================================================

/**
 * Convert on-chain lock info values into a RollockItem.
 *
 * @param amount      - locked amount in wei
 * @param lockEndTime - lock expiry (unix seconds)
 * @param lockTime    - lock creation (unix seconds)
 * @param tokenSymbol - e.g. "USDT"
 * @param usedAmount  - amount used in predictions (wei)
 * @param decimals    - token decimals
 */
export function lockInfoToRollockItem(
  amount: bigint,
  lockEndTime: number,
  lockTime: number,
  tokenSymbol: string,
  usedAmount: bigint,
  decimals: number,
): RollockItem {
  const totalAmount = weiToHuman(amount, decimals);
  const used = weiToHuman(usedAmount, decimals);
  const nowSec = Math.floor(Date.now() / 1000);

  return {
    id: `${tokenSymbol}_${lockTime}`,
    token: tokenSymbol,
    amount: totalAmount,
    usedAmount: used,
    availableAmount: Math.max(totalAmount - used, 0),
    startDate: new Date(lockTime * 1000),
    endDate: new Date(lockEndTime * 1000),
    isActive: lockEndTime > nowSec && totalAmount > 0,
  };
}

/**
 * Convert an API locker order to a RollockHistoryEntry.
 * Backend pre-divides amounts by 10^18 (see docs/backend-gaps.md), so use safeNum directly.
 */
export function apiLockerOrderToRollockHistory(
  order: ApiLockerOrder,
): RollockHistoryEntry {
  return {
    action: 'Lock',
    token: order.locker_id,
    amount: safeNum(order.amount),
    date: new Date(order.created_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    status: new Date(order.lock_end_time).getTime() > Date.now() ? 'Active' : 'Expired',
  };
}

// ============================================================================
// PORTFOLIO TRANSFORMS
// ============================================================================

/** Convert an API order to a PredictionActive row in the portfolio. */
export function apiOrderToPredictionActive(
  order: ApiOrder,
  marketId: string,
): PredictionActive {
  const { base } = parseMarketId(marketId);
  const decimals = order.base_token_decimals ?? 18;

  return {
    token: base,
    market: marketId,
    cycleId: order.cycle_id,
    time: new Date(order.created_at).toLocaleString(),
    sortTimestamp: toEpochMs(order.created_at),
    direction: sideToDirection(order.side),
    amount: safeNum(order.amount),
    betFrom: order.from_locker ? 'Rollocker' : 'Wallet',
  };
}

/** Convert an API settlement to a PredictionClosed row in the portfolio.
 *  Pass the original ApiOrder (if available) to enrich direction and betFrom. */
export function apiSettlementToPredictionClosed(
  settlement: ApiOrderSettlement,
  marketId: string,
  order?: ApiOrder,
): PredictionClosed {
  const { base } = parseMarketId(marketId);
  const settlementDecimals = settlement.base_token_decimals ?? 18;
  const reward = safeNum(settlement.reward);

  const orderDecimals = order?.base_token_decimals ?? settlementDecimals;
  const betAmount = order ? safeNum(order.amount) : reward;
  const pnlValue = reward - betAmount;

  const rawTs = order?.created_at ?? settlement.settled_at;
  return {
    token: base,
    market: marketId,
    marketId,
    cycleId: order?.cycle_id,
    time: new Date(rawTs).toLocaleString(),
    sortTimestamp: toEpochMs(rawTs),
    direction: order ? sideToDirection(order.side) : 'Yes',
    amount: betAmount,
    betFrom: order?.from_locker ? 'Rollocker' : 'Wallet',
    pnl: pnlValue > 0 ? `+${pnlValue.toFixed(2)}` : pnlValue === 0 ? '0.00' : pnlValue.toFixed(2),
    claimed: true,
  };
}

/** Convert an API order (without settlement) to a PredictionClosed row.
 *  If cycle data is provided and the cycle is settled, compute payout from cycle data.
 *  Otherwise show "Pending". */
export function apiOrderToPredictionClosed(
  order: ApiOrder,
  marketId: string,
  cycle?: ApiCycle,
): PredictionClosed {
  const { base } = parseMarketId(marketId);

  let pnl = 'Pending';
  let claimed = false;

  const orderDecimals = order.base_token_decimals ?? 18;
  const betAmount = safeNum(order.amount);

  // If cycle is settled/resolved, compute PNL from pool data
  if (cycle && (cycle.state === 'SETTLED' || cycle.state === 'RESOLVED') && cycle.result) {
    const userSide = order.side; // 'UP' or 'DOWN'
    const winSide = cycle.result; // 1=UP_WIN, 2=DOWN_WIN
    const userWon = (userSide === 'UP' && winSide === 1) || (userSide === 'DOWN' && winSide === 2);

    if (!userWon) {
      pnl = (-betAmount).toFixed(2);
    } else {
      const cycleDecimals = cycle.base_token_decimals ?? orderDecimals;
      const sharesUp = safeNum(cycle.shares_up_total);
      const sharesDown = safeNum(cycle.shares_down_total);
      const sideUp = safeNum(cycle.side_up_total);
      const sideDown = safeNum(cycle.side_down_total);
      const winSideTotalShares = winSide === 1
        ? (sharesUp > 0 ? sharesUp : sideUp)
        : (sharesDown > 0 ? sharesDown : sideDown);

      const share = safeNum(order.share) || safeNum(order.amount);
      const prizePool = cycle.prize_amount ? safeNum(cycle.prize_amount) : 0;

      if (winSideTotalShares === 0 || prizePool === 0) {
        pnl = 'Pending';
      } else {
        const reward = betAmount + (share / winSideTotalShares) * prizePool;
        const pnlValue = reward - betAmount;
        pnl = pnlValue > 0 ? `+${pnlValue.toFixed(2)}` : pnlValue === 0 ? '0.00' : pnlValue.toFixed(2);
      }
    }
  }

  return {
    token: base,
    market: marketId,
    marketId,
    cycleId: order.cycle_id,
    time: new Date(order.created_at).toLocaleString(),
    sortTimestamp: toEpochMs(order.created_at),
    direction: sideToDirection(order.side),
    amount: betAmount,
    betFrom: order.from_locker ? 'Rollocker' : 'Wallet',
    pnl,
    claimed,
  };
}

/** Convert an API settlement to a PortfolioReward row. */
export function apiSettlementToPortfolioReward(
  settlement: ApiOrderSettlement,
): PortfolioReward {
  const { base } = parseMarketId(settlement.market_id);
  const decimals = settlement.base_token_decimals ?? 18;

  return {
    token: base,
    amount: safeNum(settlement.reward),
    time: new Date(settlement.settled_at).toLocaleString(),
  };
}
